import { useState, useEffect, useRef } from "react";
import UserPanel from "./UserPanel";
import NavList from "./NavList";
import TopBar from "./TopBar";
import HealthWarning from "../HealthWarning";
import "./style.scss";
import { useQuery } from "@tanstack/react-query";
import { levelMapper } from "../../Constants";
import { useWindowSize } from "../../Helpers";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "@api/consumer";
import { Link, useLocation } from "react-router-dom";
import Crown from "./img/crown.svg?react";
import Points from "./img/points.svg?react";

export default function Navigation() {
  const [openNav, setOpenNav] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeLink, setActiveLink] = useState(false);
  const navRef = useRef(null);
  const { width } = useWindowSize();
  const HD = width >= 1280;
  const XGA = width >= 1020;
  const MOBILE = width < 1020;
  const location = useLocation();
  const isUltraInsider =
    location.pathname.includes("/subscription") ||
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const isQrScanner =
    location.pathname.startsWith("/qr-scanner") ||
    location.pathname.startsWith("/qr-register");

  const [activeSkin, setActiveSkin] = useState(() => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem("activeSkin");
    return stored || null;
  });

  const handleOpenList = (itemName) => {
    setActiveLink((prevState) => (itemName === activeLink ? null : itemName));
  };

  const { data } = useQuery({
    queryKey: ["user-details"],
    queryFn: getUserDetails,
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  useEffect(() => {
    if (XGA) {
      setOpenNav(true);
    }
    if (MOBILE) {
      setExpanded(false);
      setOpenNav(false);
    }
    if (HD) {
      setExpanded(true);
    }
  }, [MOBILE, XGA, HD]);

  const handleClickOutside = (e) => {
    if (
      document.querySelector(".onboarding") &&
      document.querySelector(".onboarding").contains(e.target) &&
      e.isTrusted
    ) {
      return;
    }

    const insideClick = navRef.current.contains(e.target);
    if (XGA && !HD) {
      if (insideClick && !expanded) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [HD, XGA, expanded]);

  useEffect(() => {
    const BLOCK_SCROLL = "block-scroll";
    if (openNav && MOBILE) {
      document.documentElement.classList.add(BLOCK_SCROLL);
    } else {
      document.documentElement.classList.remove(BLOCK_SCROLL);
    }
  }, [openNav, MOBILE]);

  const inputClass = {
    base: "navigation",
    active: "active",
    expanded: "expanded",
  };

  const handleExpandedNav = () => {
    if (XGA) {
      setExpanded(true);
    }
  };

  useEffect(() => {
    const updateSkin = () => {
      const stored = localStorage.getItem("activeSkin");
      setActiveSkin(stored || null);
    };

    updateSkin();

    window.addEventListener("skinChange", updateSkin);
    return () => {
      window.removeEventListener("skinChange", updateSkin);
    };
  }, []);

  return (
    <>
      {XGA && !HD && expanded && (
        <span
          onClick={() => setExpanded(false)}
          className="expanded-navigation-overlay"
        ></span>
      )}
      <nav
        ref={navRef}
        onClick={() => handleExpandedNav()}
        className={`${inputClass.base} ${openNav ? inputClass.active : ""} ${
          expanded ? inputClass.expanded : ""
        } ${activeSkin && !isUltraInsider && !isQrScanner ? `skin-${activeSkin}` : ""} ${
          isUltraInsider || isQrScanner ? "ultra-insider" : ""
        }`}
      >
        {!openNav && <HealthWarning />}

        <TopBar
          setOpenNav={setOpenNav}
          setExpanded={setExpanded}
          expanded={expanded}
          openNav={openNav}
        />

        <div className="bottom">
          <div className="info">
            <div className="status">
              <div className="heading">
                <p>Dein Status</p>
              </div>
              <Link
                to="/profil/points"
                onClick={() => {
                  setActiveLink(false);

                  if (!XGA) setOpenNav(false);
                }}
                className="rank"
              >
                <Crown />

                <p className="copy blue">
                  {levelMapper.get(data?.consumer?.level)}
                </p>
                {!expanded && XGA && (
                  <p className="expanded-copy blue">
                    {data?.consumer?.balance ?? 0} p
                  </p>
                )}
              </Link>
            </div>
            <div className="points">
              <div className="heading points">
                <p>Deine Punkte</p>
              </div>
              <Link
                onClick={() => {
                  setActiveLink(false);

                  if (!XGA) setOpenNav(false);
                }}
                to="/profil/points"
                className="score"
              >
                <p className="copy blue">{data?.consumer?.balance ?? 0} p</p>
                <Points />
              </Link>
            </div>
          </div>
          <NavList
            handleOpenList={handleOpenList}
            activeLink={activeLink}
            brands={data?.consumer?.brands}
            closeNav={() => {
              if (!XGA) setOpenNav(false);
            }}
          />
          <div className="panel-wrapper">
            <UserPanel
              closeNav={() => {
                setActiveLink(false);

                if (!XGA) setOpenNav(false);
              }}
            />
          </div>
        </div>
      </nav>
    </>
  );
}
